import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowLeftRight, Search, CheckCircle, AlertTriangle, Zap, AlertCircle, ChevronDown, Check } from 'lucide-react';
import PieceCard from './PieceCard';
import ModelPreview from './ModelPreview';
import { useWallet } from '../contexts/WalletContext';
import { createTradeOnChain, confirmTradeOnChain } from '../utils/tradeContract';

const MAX_PIECES_PER_TRADE = 5;

const categoryOptions = [
  { id: 'all', name: 'All Categories' },
  { id: 'Tops', name: 'Tops' },
  { id: 'Bottoms', name: 'Bottoms' },
  { id: 'Shoes', name: 'Shoes' },
  { id: 'Accessories', name: 'Accessories' },
  { id: 'Other', name: 'Other' }
];

const RequestTradeModal = ({ isOpen, onClose, targetPiece, onRequestTrade, onSuccess }) => {
  const { wallet, isConnected, publicKey, connectWallet } = useWallet();
  const [selectedPieces, setSelectedPieces] = useState([]);
  const [availablePieces, setAvailablePieces] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [tradeMessage, setTradeMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [expirationDays, setExpirationDays] = useState(7);
  const [expirationHours, setExpirationHours] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('error'); // 'error' or 'success'
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Load user's pieces for trading
  useEffect(() => {
    if (isOpen && isConnected && publicKey) {
      loadUserPieces();
    }
  }, [isOpen, isConnected, publicKey]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadUserPieces = async () => {
    if (!publicKey) {
      console.log('⚠️ No wallet connected');
      setAvailablePieces([]);
      return;
    }

    try {
      console.log('🔍 Loading pieces for wallet:', publicKey.toString());
      
      // Import supabase
      const { supabase } = await import('../lib/supabase');
      
      // Fetch minted pieces owned by this wallet from database
      const { data: pieces, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            profile_picture_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .eq('holder_wallet', publicKey.toString())
        .not('mint_address', 'is', null);

      if (error) {
        console.error('Error fetching pieces:', error);
        setAvailablePieces([]);
        return;
      }

      console.log(`✅ Found ${pieces?.length || 0} minted pieces`);

      if (!pieces || pieces.length === 0) {
        console.log('ℹ️ No minted pieces found for this wallet');
        setAvailablePieces([]);
        return;
      }

      // Format pieces for display
      const formattedPieces = pieces.map(piece => ({
        id: piece.id,
        mint_address: piece.mint_address,
        name: piece.name || 'Unnamed Piece',
        // Use actual purchase price (last_sale_price_lamports) first, then fall back to listing/base price
        price: piece.last_sale_price_lamports 
          ? (piece.last_sale_price_lamports / 1_000_000_000).toString() 
          : (piece.listing_price || piece.price || '0'),
        category: piece.category || 'Other',
        modelUrl: piece.model_url || piece.preview_glb_url,
        imageUrl: piece.preview_image_url || piece.thumbnail_url || piece.image_url,
        status: 'minted', // All pieces in this query are minted
        listing_status: piece.listing_status,
        rarity: piece.rarity || `#${Math.floor(Math.random() * 10000) + 1}`,
        views: piece.view_count || 0,
        likes: piece.like_count || 0,
        createdAt: piece.created_at
      }));

      setAvailablePieces(formattedPieces);
    } catch (error) {
      console.error('Error loading user pieces:', error);
      setAvailablePieces([]);
    }
  };

  const togglePieceSelection = (piece) => {
    setSelectedPieces(prev => {
      const isSelected = prev.some(p => p.id === piece.id);
      if (isSelected) {
        return prev.filter(p => p.id !== piece.id);
      } else {
        // Check if we've reached the limit
        if (prev.length >= MAX_PIECES_PER_TRADE) {
          setNotificationMessage(`You can only trade up to ${MAX_PIECES_PER_TRADE} pieces at once`);
          setNotificationType('error');
          setShowNotification(true);
          setTimeout(() => setShowNotification(false), 4000);
          return prev;
        }
        return [...prev, piece];
      }
    });
  };

  const handleRequestTrade = async () => {
    if (!isConnected) {
      setNotificationMessage('Please connect your wallet to request a trade');
      setNotificationType('error');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 4000);
      connectWallet();
      return;
    }

    if (selectedPieces.length === 0) {
      setNotificationMessage('Please select at least one piece to offer in trade');
      setNotificationType('error');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 4000);
      return;
    }

    // Validate minimum duration (3 days)
    const days = expirationDays === '' ? 0 : parseInt(expirationDays) || 0;
    const hours = expirationHours === '' ? 0 : parseInt(expirationHours) || 0;
    const totalHours = (days * 24) + hours;
    if (totalHours < 72) { // 72 hours = 3 days
      setNotificationMessage('Trade request must be valid for at least 3 days');
      setNotificationType('error');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 4000);
      return;
    }

    // Show custom confirmation modal
    setShowConfirmation(true);
  };

  const confirmTradeRequest = async () => {
    setShowConfirmation(false);
    setIsSubmitting(true);

    try {
      console.log('🔄 Creating P2P trade in database...');
      
      // Import trade functions
      const { createTradeOnChain } = await import('../utils/tradeContract');
      
      // Calculate expiration time
      const expirationMs = ((expirationDays === '' ? 0 : parseInt(expirationDays) || 0) * 24 + 
                            (expirationHours === '' ? 0 : parseInt(expirationHours) || 0)) * 60 * 60 * 1000;
      
      const expiresAt = new Date(Date.now() + expirationMs).toISOString();

      // Get counterparty wallet (the owner of the target piece)
      // Check both snake_case and camelCase versions of wallet fields
      const counterpartyWallet = targetPiece?.holder_wallet || targetPiece?.holderWallet || 
                                 targetPiece?.creator_wallet || targetPiece?.creatorWallet || 
                                 targetPiece?.owner;
      
      if (!counterpartyWallet) {
        console.error('❌ Cannot determine owner. Target piece data:', targetPiece);
        throw new Error('Cannot determine the owner of the target piece. Please try again.');
      }

      console.log('Initiator:', publicKey.toString());
      console.log('Counterparty:', counterpartyWallet);
      console.log('Your pieces:', selectedPieces.length);
      console.log('Their pieces:', 1);

      // Create trade in database
      const result = await createTradeOnChain(wallet, {
        counterparty: counterpartyWallet,
        initiatorPieces: selectedPieces.map(p => ({
          mint_address: p.mint_address,
          name: p.name,
          price: p.price,
          category: p.category,
          image_url: p.imageUrl
        })),
        counterpartyPieces: [{
          mint_address: targetPiece.mint_address,
          name: targetPiece.name,
          price: targetPiece.price,
          category: targetPiece.category,
          image_url: targetPiece.imageUrl
        }],
        message: tradeMessage || 'Trade request',
        expiresAt: expiresAt
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create trade request');
      }

      console.log('✅ Trade request sent:', result.requestId);
      console.log('ℹ️  Target wallet will see this in their "Incoming Trades"');
      console.log('ℹ️  Once accepted, both parties confirm and you can execute');

      // Call parent success callback
      if (onSuccess) {
        onSuccess(`Trade request sent!`);
      }
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('❌ Error requesting trade:', error);
      setNotificationMessage(error.message || 'Failed to send trade request. Please try again.');
      setNotificationType('error');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredPieces = availablePieces.filter(piece => {
    const matchesSearch = piece.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         piece.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || piece.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-6xl w-full max-h-[98vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-[#2a2a2a]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-500/20 rounded-full flex items-center justify-center">
                <ArrowLeftRight size={24} className="text-gray-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Request Trade</h2>
                <p className="text-gray-400">Offer your pieces in exchange for this item</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              <X size={24} className="text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Target Piece */}
            <div>
              <h3 className="text-base font-semibold text-white mb-3">Item You Want</h3>
              <div className="bg-[#0a0a0a] rounded-xl p-3 border border-[#2a2a2a]">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {(targetPiece?.thumbnailUrl || targetPiece?.posterUrl || targetPiece?.imageUrl || targetPiece?.preview_image_url || targetPiece?.thumbnail_url || targetPiece?.image_url) ? (
                      <img 
                        src={targetPiece?.thumbnailUrl || targetPiece?.posterUrl || targetPiece?.imageUrl || targetPiece?.preview_image_url || targetPiece?.thumbnail_url || targetPiece?.image_url} 
                        alt={targetPiece?.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (targetPiece?.previewUrl || targetPiece?.modelUrl || targetPiece?.model_url || targetPiece?.preview_glb_url) ? (
                      <div className="w-full h-full">
                        <ModelPreview
                          file={null}
                          modelUrl={targetPiece?.previewUrl || targetPiece?.modelUrl || targetPiece?.model_url || targetPiece?.preview_glb_url}
                          width="100%"
                          height="100%"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-[10px]">No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-semibold text-base truncate">{targetPiece?.name}</h4>
                    <p className="text-gray-400 text-xs">{targetPiece?.category}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-green-400 font-bold text-sm">{targetPiece?.price} SOL</span>
                      <span className="text-gray-500 text-xs">
                        ≈ ${(parseFloat(targetPiece?.price || '0') * 195.84).toFixed(2)} USD
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Pieces */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-white">Your Pieces to Offer</h3>
                <div className="px-3 py-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg">
                  <span className={`text-sm font-medium ${selectedPieces.length >= MAX_PIECES_PER_TRADE ? 'text-red-400' : 'text-gray-400'}`}>
                    {selectedPieces.length} / {MAX_PIECES_PER_TRADE}
                  </span>
                </div>
              </div>
              
              {/* Search and Filter */}
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={15} />
                  <input
                    type="text"
                    placeholder="Search your pieces..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                  />
                </div>
                
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{categoryOptions.find(opt => opt.id === filterCategory)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {categoryOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => {
                            setFilterCategory(option.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2.5 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === filterCategory
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === filterCategory && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Available Pieces Grid */}
              <div className="max-h-56 overflow-y-auto">
                {filteredPieces.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <img 
                        src="/images/piece-placeholder.png" 
                        alt="No preview available"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-gray-500 text-sm">No pieces available for trading</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4 p-2">
                    {filteredPieces.map(piece => {
                      const isSelected = selectedPieces.some(p => p.id === piece.id);
                      const isLimitReached = selectedPieces.length >= MAX_PIECES_PER_TRADE && !isSelected;
                      return (
                        <div key={piece.id} className="relative group p-1">
                          <div
                            onClick={() => togglePieceSelection(piece)}
                            className={`relative bg-gradient-to-br from-[#1a1a1a] to-[#2a2a2a] border-2 rounded-lg overflow-hidden transition-all duration-300 ${
                              isLimitReached 
                                ? 'opacity-40 cursor-not-allowed' 
                                : 'cursor-pointer hover:scale-105 hover:z-10'
                            } ${
                              isSelected 
                                ? 'border-green-400 shadow-green-400/20 shadow-lg' 
                                : 'border-gray-600 hover:border-gray-400'
                            }`}
                          >
                            {/* Thumbnail/3D Model Area */}
                            <div className="aspect-square bg-gradient-to-br from-[#2a2a2a] to-[#3a3a2a] flex flex-col items-center justify-center relative overflow-hidden">
                              {/* Show thumbnail/image if available, otherwise show model */}
                              {piece.imageUrl ? (
                                <img 
                                  src={piece.imageUrl} 
                                  alt={piece.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : piece.modelUrl ? (
                                <div className="w-full h-full">
                                  <ModelPreview
                                    file={null}
                                    modelUrl={piece.modelUrl}
                                    width="100%"
                                    height="100%"
                                  />
                                </div>
                              ) : (
                                <img 
                                  src="/images/piece-placeholder.png" 
                                  alt="No preview available"
                                  className="w-full h-full object-cover"
                                />
                              )}
                              
                              
                              {/* Selection Indicator */}
                              {isSelected && (
                                <div className="absolute inset-0 bg-green-400/10 flex items-center justify-center">
                                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Piece Info */}
                            <div className="p-2 space-y-1">
                              {/* Name */}
                              <h4 className="text-white font-bold text-xs line-clamp-1 group-hover:text-gray-200 transition-colors">
                                {piece.name.replace(/\s*#\d+.*$/, '')}
                              </h4>
                              
                              {/* Category */}
                              <div className="flex items-center gap-1">
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                                <span className="text-gray-400 text-xs">{piece.category}</span>
                              </div>
                              
                              {/* Price */}
                              <div className="text-green-400 font-bold text-xs">
                                {piece.price} SOL
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Trade Message */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Message to Seller (Optional)
            </label>
            <textarea
              value={tradeMessage}
              onChange={(e) => setTradeMessage(e.target.value)}
              placeholder="Add a message explaining why you want to trade..."
              rows={3}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent resize-none"
            />
          </div>

          {/* Expiration Duration */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trade Request Expires In
            </label>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Days</label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={expirationDays}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0);
                    setExpirationDays(value);
                  }}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                  placeholder="Days"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Hours</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={expirationHours}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : Math.max(0, Math.min(23, parseInt(e.target.value) || 0));
                    setExpirationHours(value);
                  }}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                  placeholder="Hours"
                />
              </div>
            </div>
            <p className="text-gray-500 text-xs mt-1">
              The seller will have {expirationDays !== '' && parseInt(expirationDays) > 0 ? `${expirationDays} day${parseInt(expirationDays) > 1 ? 's' : ''}` : ''}{expirationDays !== '' && parseInt(expirationDays) > 0 && expirationHours !== '' && parseInt(expirationHours) > 0 ? ' and ' : ''}{expirationHours !== '' && parseInt(expirationHours) > 0 ? `${expirationHours} hour${parseInt(expirationHours) > 1 ? 's' : ''}` : ''} to respond to your trade request (minimum 3 days)
            </p>
            {(((expirationDays === '' ? 0 : parseInt(expirationDays) || 0) * 24) + (expirationHours === '' ? 0 : parseInt(expirationHours) || 0)) < 72 && (
              <p className="text-red-400 text-xs mt-1">
                ⚠️ Trade request must be valid for at least 3 days (72 hours)
              </p>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-[#2a2a2a]">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleRequestTrade}
              disabled={!isConnected || selectedPieces.length === 0 || isSubmitting}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending Request...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  Send Trade Request
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-md w-full">
            {/* Header */}
            <div className="p-6 border-b border-[#2a2a2a]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle size={24} className="text-gray-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Confirm Trade Request</h3>
                  <p className="text-gray-400 text-sm">Review your trade before sending</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                <h4 className="text-white font-semibold mb-3">You're offering:</h4>
                <div className="space-y-2">
                  {selectedPieces.map((piece, index) => (
                    <div key={piece.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
                          {(piece?.thumbnailUrl || piece?.posterUrl || piece?.imageUrl || piece?.preview_image_url || piece?.thumbnail_url || piece?.image_url) ? (
                            <img 
                              src={piece?.thumbnailUrl || piece?.posterUrl || piece?.imageUrl || piece?.preview_image_url || piece?.thumbnail_url || piece?.image_url} 
                              alt={piece?.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (piece?.previewUrl || piece?.modelUrl || piece?.model_url || piece?.preview_glb_url) ? (
                            <ModelPreview
                              file={null}
                              modelUrl={piece?.previewUrl || piece?.modelUrl || piece?.model_url || piece?.preview_glb_url}
                              width="100%"
                              height="100%"
                            />
                          ) : (
                            <div className="text-gray-500 text-xs">No Image</div>
                          )}
                        </div>
                        <span className="text-white text-sm">{piece.name}</span>
                      </div>
                      <span className="text-green-400 font-bold text-sm">{piece.price} SOL</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                <h4 className="text-white font-semibold mb-3">In exchange for:</h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
                      {(targetPiece?.thumbnailUrl || targetPiece?.posterUrl || targetPiece?.imageUrl || targetPiece?.preview_image_url || targetPiece?.thumbnail_url || targetPiece?.image_url) ? (
                        <img 
                          src={targetPiece?.thumbnailUrl || targetPiece?.posterUrl || targetPiece?.imageUrl || targetPiece?.preview_image_url || targetPiece?.thumbnail_url || targetPiece?.image_url} 
                          alt={targetPiece?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (targetPiece?.previewUrl || targetPiece?.modelUrl || targetPiece?.model_url || targetPiece?.preview_glb_url) ? (
                        <ModelPreview
                          file={null}
                          modelUrl={targetPiece?.previewUrl || targetPiece?.modelUrl || targetPiece?.model_url || targetPiece?.preview_glb_url}
                          width="100%"
                          height="100%"
                        />
                      ) : (
                        <div className="text-gray-500 text-xs">No Image</div>
                      )}
                    </div>
                    <span className="text-white text-sm">{targetPiece?.name}</span>
                  </div>
                  <span className="text-green-400 font-bold text-sm">{targetPiece?.price} SOL</span>
                </div>
              </div>

              {tradeMessage && (
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                  <h4 className="text-white font-semibold mb-2">Your message:</h4>
                  <p className="text-gray-300 text-sm">{tradeMessage}</p>
                </div>
              )}

              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                <h4 className="text-white font-semibold mb-2">Expiration:</h4>
                <p className="text-gray-300 text-sm">
                  Trade request will expire in {expirationDays !== '' && parseInt(expirationDays) > 0 ? `${expirationDays} day${parseInt(expirationDays) > 1 ? 's' : ''}` : ''}{expirationDays !== '' && parseInt(expirationDays) > 0 && expirationHours !== '' && parseInt(expirationHours) > 0 ? ' and ' : ''}{expirationHours !== '' && parseInt(expirationHours) > 0 ? `${expirationHours} hour${parseInt(expirationHours) > 1 ? 's' : ''}` : ''}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-[#2a2a2a]">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmTradeRequest}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Zap size={16} />
                  Confirm Trade
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 z-[70] animate-in slide-in-from-top-2 duration-300">
          <div className={`${
            notificationType === 'success' 
              ? 'bg-green-600 border border-green-500/30' 
              : 'bg-red-600 border border-red-500/30'
          } rounded-xl p-4 shadow-lg max-w-sm`}>
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 ${
                notificationType === 'success' ? 'bg-green-500' : 'bg-red-500'
              } rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                {notificationType === 'success' ? (
                  <CheckCircle size={14} className="text-white" />
                ) : (
                  <AlertTriangle size={14} className="text-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{notificationMessage}</p>
              </div>
              <button
                onClick={() => setShowNotification(false)}
                className="text-white/70 hover:text-white transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to create file from data URL
const createFileFromDataUrl = (dataUrl, name, type) => {
  // Check if it's actually a data URL
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    // If it's a regular URL, return null and handle it differently
    return null;
  }
  
  try {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) return null;
    
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], name, { type: type || mime });
  } catch (error) {
    console.error('Error creating file from data URL:', error);
    return null;
  }
};

export default RequestTradeModal;
